<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { MUButton, useServerApi, MContainer, MContainerGrow, MContainerRow, MContainerColumn, TitleSmall } from "@xlabmedical/medic-web-framework";
import { ProjectModel } from "@xlabmedical/implant-planner";
import { useRouter } from "vue-router";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import RgSegmentationStatus from "@/components/RgSegmentationStatus.vue";
import { SegmentationStatus } from "@/models/SegmentationStatus";
import RgProjectImplantList from "@/components/ProjectItem/RgProjectImplantList.vue";
import Popover from "primevue/popover";
import RgMenuRedesign from "@/components/RgMenuRedesign.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
    project: ProjectModel;
    exportToAlfred?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits(["reload-patient-store", "segmentationTriggered", "exportToAlfred", "sendToWeblinkClicked", "exportClicked"]);

const confirm = useConfirm();
const toast = useToast();

const api = useServerApi();
const router = useRouter();

const implantListGrid = ref();

const onOpenClick = async () => {
    await router.push({
        name: "ImplantPlannerProjectLoaderView",
        params: {
            projectHash: props.project.uuid,
        },
    });
};

const exportToAlfredClicked = async () => {
    emit("exportToAlfred", props.project.id);
};

const onDeleteClick = async () => {
    confirm.require({
        message: t("web-rg.deleteProjectPrompt", { filename: props.project.projectFilename }),
        header: t("web-rg.deleteProject"),
        icon: "pi pi-exclamation-triangle",
        accept: async () => {
            await api.deleteProjectFile(props.project.patientName, props.project.projectFilename);
            if (props.project.mobileFilename) {
                await api.deleteProjectFile(props.project.patientName, props.project.mobileFilename);
            }
            toast.add({
                severity: "info",
                summary: "Project deleted",
                detail: "You have deleted project " + props.project.projectFilename,
                life: 3000,
            });
            confirm.close();
            emit("reload-patient-store");
        },
        reject: () => {
            confirm.close();
        },
    });
};

const onSegmentClick = () => {
    emit("segmentationTriggered", props.project.id);
};

const imageUrl = ref("");
const imageLoading = ref(props.project.imageLoading);
watch(
    () => props.project.imageLoading,
    () => {
        imageLoading.value = props.project.imageLoading;
        if (!props.project.imageLoading) {
            imageUrl.value = `data:image/png;base64,${props.project.imageBuffer}`;
        }
    },
);
if (!props.project.imageLoading) {
    imageUrl.value = `data:image/png;base64,${props.project.imageBuffer}`;
}

function getProjectName(name: string) {
    return name.replace(".zip", "");
}

const showImplantListWindow = (event: Event) => {
    implantListGrid.value.toggle(event);
};

const hasImplants = computed(() => {
    return (props.project.implantList ?? []).length > 0;
});

const projectItemMenuItems = computed(() => {
    const items: (
        | {
              label: string;
              icon: string;
              disabled?: boolean;
              separator?: undefined;
          }
        | {
              separator: boolean;
              label?: undefined;
              icon?: undefined;
          }
    )[] = [
        {
            label: "Send To Weblink",
            icon: "pi pi-cloud-upload",
            disabled: true,
        },
        {
            label: "Export",
            icon: "pi pi-file-export",
        },
        {
            label: "Delete",
            icon: "pi pi-plus",
        },
    ];
    if (props.project.segmentationStatus === SegmentationStatus.NOT_STARTED) {
        items.push({
            separator: true,
        });
        items.push({
            label: "Segment",
            icon: "pi pi-sparkles",
        });
    }
    return [
        {
            items: items,
        },
    ];
});

const onMenuItemClicked = (label: string) => {
    if (label === "Delete") {
        onDeleteClick();
    } else if (label === "Segment") {
        onSegmentClick();
    } else if (label === "Sent To Weblink") {
        emit("sendToWeblinkClicked", props.project.id);
    } else if (label === "Export") {
        emit("exportClicked", props.project.id);
    }
};
</script>
<style lang="scss" scoped>
::v-deep(.p-progressbar .p-progressbar-value) {
    border: 0 none;
    margin: 0;
    background: #43c261 !important;
}

:deep(.p-divider-solid.p-divider-horizontal:before) {
    border-bottom: outset;
    border-top: 0.5px solid var(--p-divider-border-color) !important;
}
</style>

<template>
    <Card class="rg-project-item">
        <template #header>
            <MContainerRow class="gap-2 pt-4 pl-4">
                <MContainerGrow class="shrink basis-0" style="max-width: calc(100% - 60px) !important">
                    <MContainerColumn class="w-full">
                        <span class="pl-project-title overflow-text">{{ getProjectName(project.projectFilename) }}</span>
                        <span class="text-l" style="color: limegreen">{{ $t("web-rg.projectReady") }}</span>
                    </MContainerColumn>
                </MContainerGrow>
                <MContainer v-if="exportToAlfred"><MUButton @click="exportToAlfredClicked" :label="$t('web-rg.exportToAlfred')" /></MContainer>
                <MContainer v-else-if="!exportToAlfred"><RgMenuRedesign class="web-rg-icon-btn-40" :items="projectItemMenuItems" @itemClicked="onMenuItemClicked" /></MContainer>
            </MContainerRow>
            <div class="img-card-size-redesign cursor-pointer" v-if="!imageLoading" @click="onOpenClick">
                <img :src="imageUrl" class="w-full h-full object-contain top-radius img-card-size-redesign" style="object-fit: cover" alt="" draggable="false" />
            </div>
            <div v-if="imageLoading" class="img-card-size-redesign"><Skeleton class="w-full img-card-size-redesign" style="border-radius: 0 !important" /></div>
        </template>
        <template #content>
            <div style="overflow-y: auto; overflow-x: hidden">
                <MContainer>
                    <div class="flex-auto">
                        <MContainerColumn>
                            <TitleSmall>{{ $t("shared.date") }}</TitleSmall>
                            <span>{{ project.lastModified.toLocaleDateString() }}</span>
                            <MContainerColumn v-if="!hasImplants" class="pt-2">
                                <TitleSmall>{{ $t("shared.tooth") }}</TitleSmall>
                                <span class="ml-1">-</span>
                            </MContainerColumn>
                        </MContainerColumn>
                    </div>
                    <div class="flex-auto">
                        <MContainerColumn>
                            <TitleSmall>{{ $t("shared.time") }}</TitleSmall>
                            <span>{{ project.lastModified.toLocaleTimeString() }}</span>
                            <MContainerColumn v-if="!hasImplants" class="pt-2">
                                <TitleSmall>{{ $t("shared.implant") }}</TitleSmall>
                                <span class="ml-1">-</span>
                            </MContainerColumn>
                        </MContainerColumn>
                    </div>
                    <div class="flex-auto">
                        <MContainerColumn>
                            <TitleSmall>{{ $t("shared.lastUpdateBy") }}</TitleSmall>
                            <span>{{ project.user }}</span>
                            <MContainerColumn v-if="!hasImplants" class="pt-2">
                                <TitleSmall>{{ $t("shared.abutment") }}</TitleSmall>
                                <span class="ml-1">-</span>
                            </MContainerColumn>
                        </MContainerColumn>
                    </div>
                    <div class="flex-auto">
                        <MContainerColumn>
                            <TitleSmall>{{ $t("web-rg.segmentation") }}</TitleSmall>
                            <RgSegmentationStatus :segmentation-status="project.segmentationStatus" />
                        </MContainerColumn>
                    </div>
                </MContainer>
                <MContainer v-if="hasImplants" class="col-12-no-padding" style="height: 45px !important; overflow: hidden">
                    <MContainer class="w-full pt-2">
                        <MContainerGrow><RgProjectImplantList :implant-list="project.implantList" /></MContainerGrow>
                        <div class="justify-end">
                            <Button class="mr-6" v-if="(project.implantList ?? []).length > 1" icon="pi pi-ellipsis-h" rounded outlined @click="showImplantListWindow" />
                            <Popover ref="implantListGrid"><RgProjectImplantList :implant-list="project.implantList" :isPopover="true" /></Popover>
                        </div>
                    </MContainer>
                </MContainer>
            </div>
        </template>
        <!--
        <template #footer>
            <Divider />
            <div v-if="!imageLoading" class="flex flex-row justify-center gap-2" :class="[project.implantList?.length === 0 ? 'mt-2' : '']">
                <MUButton
                    class="h-2-2rem"
                    iconSrc="/icons/file_open_white.svg"
                    :label="$t('web-rg.open')"
                    @click="onOpenClick"
                    v-if="!inSegmentationProcess"
                    :iconSrcHeight="'28px'" />
                <MUButton
                    class="h-2-2rem"
                    iconSrc="/icons/delete_white.svg"
                    :label="$t('shared.delete')"
                    @click="onDeleteClick"
                    v-if="!inSegmentationProcess"
                    :iconSrcHeight="'28px'" />
                <MUButton
                    class="h-2-2rem"
                    iconSrc="/icons/toolbar_icons/ik_xray_28.svg"
                    :label="$t('web-rg.segment')"
                    @click="onSegmentClick"
                    v-if="showSegmentationButton"
                    :iconSrcHeight="'28px'" />
            </div>
        </template>
        -->
    </Card>
    <br />
</template>
